import { Space } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { AddPersonRolesModal } from "~components/AddPersonRolesModal";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { RoleStatuses } from "~enums/roleStatuses.ts";
import { useFetchRoles } from "~hooks/apiHooks/useFetchRoles.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useSeasonOfficialService } from "~pages/Officials/SeasonOfficialsListPage/hooks/useSeasonOfficialService.ts";
import type { UpdateSeasonOfficialRole } from "~pages/Officials/SeasonOfficialsListPage/types/seasonOfficialRole.ts";
import { transformRoleResponse } from "~utilities/transformRoleResponse.ts";
import { defaultSeasonOfficialsExportFieldNames, seasonOfficialsExportFields } from "./constants/exportFields";
import { SeasonOfficialsTableFilters } from "./enums/seasonOfficialsTableFilters";
import { useGetSeasonOfficialsTableColumns } from "./hooks/useGetSeasonOfficialsTableColumns";
import { useGetSeasonOfficialsTableFilters } from "./hooks/useGetSeasonOfficialsTableFilters";
import type { SeasonOfficialsTableRow } from "./types/seasonOfficialsTableRow";

const ROLE_STATUSES = Object.values(RoleStatuses);
const DEFAULT_SORTER = { columnKey: "role", order: "ascend" as const };

const SeasonOfficialsListPage = () => {
  const { customerId, sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { seasonOfficialRoles, uiConfiguration } = useSportConfiguration();
  const { hasPermission } = useUserPermissions();
  const paginationConfig = useHandlePagination(20);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("role");
  const [isMassEditMode, setIsMassEditMode] = useState(false);
  const [isAddOfficialModalOpen, setIsAddOfficialModalOpen] = useState(false);
  const dataTableImportConfiguration = uiConfiguration.pages.seasonOfficials.listPage.importConfig;
  const {
    addSeasonOfficialRole,
    isAddPending,
    updateSeasonOfficialRole,
    deleteSeasonOfficialRole,
    isDeletePending,
    isUpdatePending,
    importSeasonOfficials,
    exportSeasonOfficials,
    isExportPending,
  } = useSeasonOfficialService();
  const { currentFilter, filters, handleFilterChange } = useGetSeasonOfficialsTableFilters({
    officialRoles: seasonOfficialRoles,
    roleStatuses: ROLE_STATUSES,
  });

  const handleSearch = useCallback(
    (value: string) => {
      paginationConfig.resetPagination();
      setSearchText(value);
    },
    [paginationConfig],
  );

  const handleFiltersChange = useCallback(
    (updatedFilter: Record<string, unknown>) => {
      paginationConfig.resetPagination();
      handleFilterChange(updatedFilter);
    },
    [handleFilterChange, paginationConfig],
  );

  const listParams = useMemo(() => {
    const selectedRoles = currentFilter[SeasonOfficialsTableFilters.Role];
    const selectedStatus = currentFilter[SeasonOfficialsTableFilters.Status];
    const selectedGender = currentFilter[SeasonOfficialsTableFilters.Gender];

    return {
      limit: paginationConfig.pageSize.toString(),
      nameFullLocalContains: searchText,
      offset: paginationConfig.offset.toString(),
      role: (selectedRoles?.length ? selectedRoles : seasonOfficialRoles).join(","),
      gender: selectedGender?.value,
      sortBy,
      ...(selectedStatus ? { status: selectedStatus } : {}),
    };
  }, [currentFilter, paginationConfig.offset, paginationConfig.pageSize, searchText, seasonOfficialRoles, sortBy]);

  const { data, isFetching, refetch } = useFetchRoles(listParams, transformRoleResponse, seasonOfficialRoles);

  const updateRoleAndRefetch = useCallback(
    async (payload: UpdateSeasonOfficialRole) => {
      const result = await updateSeasonOfficialRole(payload);

      if (result) {
        await refetch();
      }
    },
    [refetch, updateSeasonOfficialRole],
  );

  const columns = useGetSeasonOfficialsTableColumns({
    isMassEditMode,
    officialRoles: seasonOfficialRoles,
    roleStatuses: ROLE_STATUSES,
    updateRole: updateRoleAndRefetch,
  });

  const navigateToPerson = (personId: string) => {
    navigate(`/${customerId}/admin/${sport}/${organizationId}/persons/manage/${personId}`);
  };

  const canManagePerson = hasPermission(PermissionNames.PERSON, PermissionOperations.UPDATE);
  const canAddOfficial =
    hasPermission(PermissionNames.ROLES, PermissionOperations.CREATE) && hasPermission(PermissionNames.PERSON, PermissionOperations.READ);

  return (
    <>
      {canAddOfficial && (
        <AddPersonRolesModal
          open={isAddOfficialModalOpen}
          setOpen={setIsAddOfficialModalOpen}
          roles={seasonOfficialRoles}
          statuses={ROLE_STATUSES}
          onAddRole={addSeasonOfficialRole}
          isAddPending={isAddPending}
          refetch={refetch}
        />
      )}
      <DataTable<SeasonOfficialsTableRow>
        handleRefresh={async () => {
          await refetch();
        }}
        title={formatMessage({ id: "statistics.seasonOfficials" })}
        columns={columns}
        rowActions={{
          base: {
            delete: {
              onClick: (row) => {
                void deleteSeasonOfficialRole(row.roleId).then((deleted) => {
                  if (deleted) {
                    void refetch();
                  }
                });
              },
              itemName: (row) => row.nameFullLocal || row.personId,
              loading: isDeletePending,
            },
          },
          custom: {
            manage: {
              hidden: () => !canManagePerson,
              renderAction: (row) => (
                <Space onClick={() => navigateToPerson(row.personId)}>
                  <i className="fas fa-eye mr-2" />
                  <span>{formatMessage({ id: "manage" })}</span>
                </Space>
              ),
            },
          },
        }}
        rowKey="roleId"
        rowNameKey="nameFullLocal"
        dataSource={data ?? []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasonOfficials`}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.seasonOfficials`,
          defaultSorter: DEFAULT_SORTER,
          onSortChange: setSortBy,
        }}
        isDataLoading={isFetching || isUpdatePending || isDeletePending}
        handleAdd={canAddOfficial ? () => setIsAddOfficialModalOpen(true) : undefined}
        addButtonIntlKey="officials.add"
        exportConfig={{
          defaultFields: defaultSeasonOfficialsExportFieldNames,
          groupedFields: seasonOfficialsExportFields,
          handleExport: exportSeasonOfficials,
          loading: isExportPending,
          localStorageKey: "export.seasonOfficials",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: importSeasonOfficials,
        }}
        searchConfig={{
          handleSearch,
          searchText,
        }}
        filtersConfig={{
          currentFilter,
          filters,
          handleFilterChange: handleFiltersChange,
        }}
        onMassEditModeChangedHandler={setIsMassEditMode}
        massEditMode={isMassEditMode}
        permissionNames={[PermissionNames.ROLES]}
        dataTableUiConfiguration={uiConfiguration.pages.seasonOfficials.listPage}
        paginationConfig={{ ...paginationConfig, hasNext: data?.length === paginationConfig.pageSize }}
      />
    </>
  );
};

export default SeasonOfficialsListPage;
